import React, { useEffect, useMemo, useRef } from 'react';
import { Icon } from '@/components/ui/Icon';
import { ConvRow } from './ConvRow';
import { useT, type LabelKey } from '@/i18n/labels';
import { useConversationStore, selectFolderRows } from '@/stores/conversationStore';
import { useAuthStore } from '@/stores/authStore';
import { usePermission } from '@/hooks/usePermission';
import { useConsole } from '@/console/ConsoleContext';
import { useFolderCounts } from '@/console/useFolderCounts';
import { closedStatusKey, categoryFolderId, folderLabel, folderMatcher, isSearchFolder, searchQueryOf } from '@/console/folders';
import {
  LIFECYCLE_TABS,
  folderPageKey,
  lifecycleMatches,
  tabAppliesTo,
  writeStoredLifecycleTab,
  type LifecycleTab,
} from '@/console/lifecycle';
import type { FolderId } from '@/types';

/** More than this many rows (one page) and the list says it has loaded everything once the end is reached. */
export const LIST_PAGE_SIZE = 20;

/** The sentinel loads the next page this far (px) before the end of the list is reached. */
const SENTINEL_MARGIN = '0px 0px 120px 0px';

/** What an empty folder says. Anything not listed (a category, a tag) falls back to the generic sentence. */
const EMPTY_KEY: Readonly<Record<string, LabelKey>> = {
  mine: 'cl_empty_mine',
  'my-consult': 'cl_empty_my_consult',
  'my-takeover': 'cl_empty_my_takeover',
  'my-released': 'cl_empty_my_released',
  'all-open': 'cl_empty_all_open',
  drafts: 'cl_empty_drafts',
};

/** The tab buttons, in order. */
const TAB_LABEL: Readonly<Record<LifecycleTab, LabelKey>> = {
  all: 'cl_tab_all',
  'need-help': 'cl_tab_need_help',
  bot: 'cl_tab_bot',
  agent: 'cl_tab_agent',
};

interface ConversationListProps {
  folder: FolderId;
  activeId: string | null;
  onSelect: (sessionId: string) => void;
}

/**
 * The conversation list of ONE folder (ADR-033, D13): twenty rows a page, the next page loaded by an
 * IntersectionObserver sentinel as the end comes into view (and by a "Load more" button, which is also the only way on a
 * browser without one), skeleton rows while the first page loads, and a different empty sentence per folder.
 *
 * <b>The rows are the server's.</b> They come from `selectFolderRows` (the pages the server listed, plus pushed rows the
 * client rules can place), never from a client-side rebuild of the folder. Rows the viewer must not see - the hidden set,
 * the spam tombstones - are filtered by that selector, and a conversation that has no message yet (a widget that was
 * merely opened) is not listed.
 * <b>The header count is the server's count</b>, not the number of rows loaded so far.
 * <b>The search view (`search:<q>`, PR-C3) is one more folder</b>: the rows are the server's answer to `q` (every state the
 * viewer may see, closed included), paged by this same sentinel with the same 20 a page. It has no count (the server counts
 * folders, not queries), a header that says what it is, and its own empty sentence. The client never re-filters it: what
 * the server did not send is not shown, and what it did send is not narrowed or widened here.
 *
 * <b>Four lifecycle tabs</b> (All / Need help / Bot handling / Agent handling) sit under the header of every list folder.
 * The choice is the store's `lifecycleTab` (remembered per viewer in localStorage), goes to the server as `lifecycle` on the
 * list and the counts, and is part of the page cache key, so switching tabs never shows another tab's rows. A search and the
 * drafts feed have no tabs.
 */
export const ConversationList: React.FC<ConversationListProps> = ({ folder, activeId, onSelect }) => {
  const { t, lang } = useT();
  const { loadMore, reloadSessions } = useConsole();
  const { roles } = usePermission();
  const me = useAuthStore((s) => s.user?.agentId);
  const { countOf } = useFolderCounts();

  const conversations = useConversationStore((s) => s.conversations);
  const folderPages = useConversationStore((s) => s.folderPages);
  const spamTombstones = useConversationStore((s) => s.spamTombstones);
  const hiddenSessions = useConversationStore((s) => s.hiddenSessions);
  const stateDefs = useConversationStore((s) => s.stateDefs);
  const tagCatalog = useConversationStore((s) => s.tagCatalog);
  const setSearchQuery = useConversationStore((s) => s.setSearchQuery);
  const storedTab = useConversationStore((s) => s.lifecycleTab);
  const setLifecycleTab = useConversationStore((s) => s.setLifecycleTab);

  // The tab narrows every LIST folder; a search and the drafts feed are not narrowed by it.
  const showTabs = tabAppliesTo(folder);
  const tab: LifecycleTab = showTabs ? storedTab : 'all';
  const pageKey = folderPageKey(folder, tab);
  const closedKey = closedStatusKey(stateDefs);

  const chooseTab = (next: LifecycleTab) => {
    if (next === storedTab) return;
    writeStoredLifecycleTab(next);
    setLifecycleTab(next);
  };

  const page = folderPages[pageKey];
  const loading = !!page?.loading;
  const failed = !!page?.error;
  const hasMore = !!page?.hasMore;
  // The drafts folder has no page of its own (its rows come from the drafts feed, OP-582), so it is never "waiting for page 1".
  const firstLoadPending = folder === 'drafts' ? false : !page || page.loadedAt === null;

  const folderRows = useMemo(() => {
    // The client copy of the folder rule AND the tab's rule, for rows a push just moved between server pages.
    const inFolder = folderMatcher(folder, { me, roles, closedKey });
    const rows = selectFolderRows(
      { conversations, folderPages, spamTombstones, hiddenSessions },
      folder,
      (c) => lifecycleMatches(tab, c) && inFolder(c),
      pageKey,
    );
    // The drafts folder lists every conversation with a draft waiting, whether or not the customer has typed anything;
    // every other folder leaves out a row KNOWN to have no message (a session created live by a widget that was merely opened).
    return folder === 'drafts' ? rows : rows.filter((c) => c.messageCount !== 0);
  }, [conversations, folderPages, spamTombstones, hiddenSessions, folder, me, roles, closedKey, tab, pageKey]);

  const rows = folderRows;
  const isSearch = isSearchFolder(folder);
  const searchedFor = isSearch ? searchQueryOf(folder) : null;

  const label = folderLabel(folder, stateDefs, tagCatalog, t, lang);
  // A search has no count: the server counts folders, never a query (and countOf would answer "0" for an id it never sent).
  const count = isSearch ? null : countOf(folder);

  // ── Infinite scroll ───────────────────────────────────────────────────────────────────────────────────────────
  const scrollerRef = useRef<HTMLUListElement>(null);
  const sentinelRef = useRef<HTMLLIElement>(null);
  // The observer's callback reads the CURRENT paging state through a ref, so it never needs to be torn down for it.
  const canLoadRef = useRef(false);
  canLoadRef.current = hasMore && !loading && !failed;
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        if (canLoadRef.current) void loadMoreRef.current();
      },
      { root: scrollerRef.current, rootMargin: SENTINEL_MARGIN },
    );
    io.observe(el);
    return () => io.disconnect();
    // The sentinel element is re-created when it appears or disappears; re-arm on exactly that.
  }, [hasMore, folder, pageKey]);

  // A new folder (or tab) starts at the top.
  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = 0;
  }, [folder, pageKey]);

  const retry = () => {
    // A failed NEXT page is asked for again from where it stopped; a failed first page is loaded again from the top.
    if (folderRows.length > 0 && hasMore) void loadMore();
    else void reloadSessions();
  };

  // The closed status's folder has its own sentence (it replaces the retired all-closed folder).
  const emptyKey: LabelKey = closedKey && folder === categoryFolderId(closedKey) ? 'cl_empty_all_closed' : EMPTY_KEY[folder] ?? 'cl_empty';

  const showSkeleton = rows.length === 0 && (firstLoadPending || loading) && !failed;
  const showError = failed;
  // Nothing in the folder at all: the folder's own sentence.
  const showEmpty = !showSkeleton && !showError && folderRows.length === 0 && !hasMore && !firstLoadPending;

  return (
    <aside className="cl" aria-label={label} data-testid="conversation-list" data-folder={folder}>
      <div className="cl-head">
        <h2 className="cl-title" dir="auto">
          {label}
          {!isSearch && (
            <>
              {' '}
              <span className="cl-num" data-testid="cl-count">
                ({count === null ? '–' : count})
              </span>
            </>
          )}
        </h2>
      </div>

      {showTabs && (
        <div className="cl-tabs" role="tablist" aria-label={t('cl_tabs_label')} data-testid="cl-tabs">
          {LIFECYCLE_TABS.map((id) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={'cl-tab' + (tab === id ? ' active' : '')}
              onClick={() => chooseTab(id)}
              data-testid={`cl-tab-${id}`}
            >
              {t(TAB_LABEL[id])}
            </button>
          ))}
        </div>
      )}

      {searchedFor !== null && (
        <div className="cl-filter" role="status" data-testid="cl-search-note">
          <span dir="auto">{t('cl_search_note', { q: searchedFor })}</span>
          {/* Emptying the text is the one way out: the top bar sees it and leaves the search view at once. */}
          <button
            type="button"
            className="ac-icon-btn"
            onClick={() => setSearchQuery('')}
            aria-label={t('cl_search_clear')}
            data-testid="cl-search-clear"
          >
            <Icon name="x" size={14} />
          </button>
        </div>
      )}

      <ul className="cl-rows" ref={scrollerRef} data-testid="cl-rows">
        {showSkeleton &&
          [0, 1, 2, 3, 4].map((i) => (
            <li key={`skeleton-${i}`} className="cl-skel" data-testid="cl-skeleton" aria-hidden="true">
              <span className="cl-skel-av" />
              <span className="cl-skel-lines">
                <span className="cl-skel-line" />
                <span className="cl-skel-line short" />
              </span>
            </li>
          ))}

        {showError && (
          <li className="cl-error" role="alert" data-testid="cl-error">
            <span>{t('cl_load_failed')}</span>
            <button type="button" className="ac-btn ghost sm" onClick={retry} data-testid="cl-retry">
              {t('cl_retry')}
            </button>
          </li>
        )}

        {showEmpty && (
          <li className="cl-empty" data-testid="cl-empty">
            {searchedFor !== null ? t('cl_empty_search', { q: searchedFor }) : t(emptyKey)}
          </li>
        )}

        {rows.map((c) => (
          <li key={c.sessionId} className="cl-item">
            {/* Every folder shows the row's owner chip (incl. "Team shared"): the Team folders are team-wide by STATE now, so
                an owned and an unowned conversation sit side by side in them (PR-A). */}
            <ConvRow c={c} active={c.sessionId === activeId} onClick={() => onSelect(c.sessionId)} />
          </li>
        ))}

        {hasMore && (
          <li ref={sentinelRef} className="cl-loading" data-testid="cl-sentinel" aria-live="polite">
            {loading ? (
              <span data-testid="cl-loading-more">
                <Icon name="loader" size={16} className="ac-spin" /> {t('cl_loading_more')}
              </span>
            ) : (
              <>
                {count !== null && count >= folderRows.length && (
                  <span className="cl-showing" data-testid="cl-showing">
                    {t('cl_showing', { n: folderRows.length, total: count })}
                  </span>
                )}
                <button type="button" className="ac-btn ghost sm" onClick={() => void loadMore()} data-testid="cl-load-more">
                  {t('contact_load_more')}
                </button>
              </>
            )}
          </li>
        )}
        {!hasMore && !firstLoadPending && !failed && folderRows.length > LIST_PAGE_SIZE && (
          <li className="cl-done" data-testid="cl-all-loaded">
            {t('cl_all_loaded', { n: folderRows.length })}
          </li>
        )}
      </ul>
    </aside>
  );
};
